import Loader from "@/components/common/loader";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth-context";
import useDebounce from "@/hooks/useDebounce";
import { fetchEnrollments } from "@/store/features/enroll-slice";
import { RootState } from "@/store/root-reducer";
import { AppDispatch } from "@/store/store";
import { formatFilters } from "@/util/query-builder";
import {
  ColumnFiltersState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columns, Enrollment } from "./components/columns";
import EnrollTable from "./components/enrolls-table";

export default function EnrollPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();

  const data = useSelector((state: RootState) => state.enrollment.data);
  const fetchLoading = useSelector(
    (state: RootState) => state.enrollment.fetchLoading
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [search, setSearch] = useState<string>("");
  const debouncedValue = useDebounce(search, 3000);

  // Effect to handle filter changes
  useEffect(() => {
    const formattedFilters = formatFilters(columnFilters);
    // You can use formattedFilters here to make API calls

    dispatch(
      fetchEnrollments({
        params: {
          limit: pagination.pageSize,
          page: pagination.pageIndex + 1,
          filters: {
            ...formattedFilters.filter,
            ...(debouncedValue !== ""
              ? {
                  $or: [
                    {
                      fullName: {
                        $like: debouncedValue,
                      },
                    },

                    {
                      email: {
                        $like: debouncedValue,
                      },
                    },

                    {
                      mobile: {
                        $like: debouncedValue,
                      },
                    },

                    {
                      "course.name": {
                        $like: debouncedValue,
                      },
                    },
                  ],
                }
              : {}),
            is_enrolled: true,
          },
          populate: "course",
          ...(sorting?.[0]?.id
            ? {
                sort: `${sorting?.[0]?.id}:${
                  sorting?.[0]?.desc ? "DESC" : "ASC"
                }`,
              }
            : {}),
        },
      })
    );
  }, [
    columnFilters,
    dispatch,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    user?.id,
    debouncedValue,
  ]);

  // Memoize the data for the table to update properly when filters change
  const filteredData = useMemo(() => {
    return (data?.data ?? []) as Enrollment[];
  }, [data]);

  const table = useReactTable<Enrollment>({
    data: filteredData,
    pageCount: Math.ceil(data?.meta?.totalPages || 0),
    columns: columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex items-start justify-between gap-10">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Enrollments</h2>
          <span className="text-sm">Total records : {data?.meta?.total}</span>
        </div>

        <div className="flex items-center space-x-2 w-full">
          <Input
            placeholder="Search enrollments by student name, email, address, mobile and course..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full"
          />
          {/* <Button onClick={() => navigate("/enrollments/add")}>
            Add Enrollment
          </Button> */}
        </div>
      </div>

      {fetchLoading ? (
        <Loader />
      ) : (
        <EnrollTable
          pagination={pagination}
          setPagination={setPagination}
          table={table}
        />
      )}
    </div>
  );
}
